import { API_METHODS } from '@compass/client/constants/api-methods';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAPIPermission } from '@/hooks/usePermissions';
import type {
  CallBreakdown,
  IneligibleReasonBreakdown,
  MetadataBreakdownEntry,
} from '@/pages/analytics/types';
import { CHART_COLOR_VARS } from '@/pages/analytics/utils/chartPalette';
import {
  buildQcMetadataFilterPath,
  formatIneligibleReasonBucketTitle,
  type QcDrilldownScope,
} from '@/pages/qc/utils';

import { ChartCard } from './ChartCard';
import type { ChartDownloadFormat } from './ChartDownloadMenu';
import { ChartEmptyState } from './ChartStates';
import { DonutBreakdown, formatShare, formatValue } from './DonutBreakdown';
import { InfoTooltip } from './InfoTooltip';

interface CallBreakdownGridProps {
  data?: CallBreakdown;
  loading?: boolean;
  evaluatedCalls?: number;
  exportSectionId?: string;
  /** Unprefixed org id. When present AND the user can read evaluations, metadata
   *  breakdown value rows become clickable and navigate to the QC list filtered by
   *  that metadata.<key>=<value>. When absent, for the direction section, or when
   *  the user lacks evaluation read permission, rows are non-interactive. */
  organizationId?: string | null;
  /** Analytics filters (date range, programs, agent) carried into the QC drill-down. */
  drilldownScope?: QcDrilldownScope;
}

interface SectionRow {
  label: string;
  callCount: number;
  /** Raw metadata value (unformatted), used to build the QC filter. */
  value: string;
}

interface BreakdownSection {
  title: string;
  rows: SectionRow[];
  /** Raw metadata key for this section, or null for the (non-filterable)
   *  direction section. Drives whether rows are clickable. */
  metadataKey: string | null;
  /** Whether this metadata key is registered as searchable. Non-searchable keys
   *  can't be filtered in the QC list, so their rows are never drillable. */
  searchable: boolean;
}

interface ColoredSectionRow extends SectionRow {
  color: string;
}

interface ColoredBreakdownSection extends BreakdownSection {
  rows: ColoredSectionRow[];
  totalCalls: number;
}

// Fold entries whose values collapse to the same display label. The backend
// groups metadata values case-sensitively (ClickHouse GROUP BY on the raw
// string), while formatValue is case/format-insensitive — so inconsistently
// cased values (e.g. "None" and "none") arrive as separate rows that render
// with an identical label. Sum their counts into one row and keep the
// highest-count member's raw value as the representative (drives display and
// the drilldown equality filter).
export const toRows = (rows: MetadataBreakdownEntry[] = []): SectionRow[] => {
  const byLabel = new Map<string, { row: SectionRow; topCount: number }>();
  for (const entry of rows) {
    const value = entry.value ?? '';
    const label = value || '—';
    const key = formatValue(label);
    const existing = byLabel.get(key);
    if (!existing) {
      byLabel.set(key, {
        row: { label, callCount: entry.call_count, value },
        topCount: entry.call_count,
      });
    } else {
      existing.row.callCount += entry.call_count;
      if (entry.call_count > existing.topCount) {
        existing.topCount = entry.call_count;
        existing.row.label = label;
        existing.row.value = value;
      }
    }
  }
  return [...byLabel.values()]
    .map(entry => entry.row)
    .sort(
      (left, right) => right.callCount - left.callCount || left.label.localeCompare(right.label),
    );
};

const CALL_ANALYTICS_INFO =
  'Breakdown of evaluated calls by direction, contact role, call reason, and call events. Shows call volume for each dimension.';
// TODO(AQ-516): remove hardcoded keys. skill/campaign/team (AQ-437) and the
// Genesys queue are call-sourced dimensions, not evaluated signals, so they should
// not render as call-breakdown metadata charts. Hide them here as an interim; the real fix is a
// metadata-registry attribute the UI consults instead of hardcoding key names.
// See https://100ms.atlassian.net/browse/AQ-516
const HIDDEN_METADATA_SECTION_KEYS = new Set([
  'ae-pqc-detected',
  'skill-name',
  'campaign-name',
  'team-name',
  'queue-name',
]);
const BreakdownGridSkeleton: React.FC = () => (
  <section className="space-y-4" data-export-pdf-block>
    <header className="space-y-1">
      <div className="flex items-center gap-1.5">
        <h3 className="text-sm font-semibold leading-5 text-foreground">Call Analytics</h3>
        <InfoTooltip content={CALL_ANALYTICS_INFO} />
      </div>
      <Skeleton className="h-4 w-64" />
    </header>
    <div className="space-y-3" role="status" aria-busy="true" aria-label="Loading call breakdowns">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, cardIndex) => (
          <Card key={cardIndex} className="p-4" data-export-pdf-block>
            <div className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-4 w-28" />
              </div>
              <div className="flex justify-center">
                <Skeleton className="h-32 w-32 rounded-full" />
              </div>
              <Skeleton className="h-3 w-32" />
            </div>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

const colorForIndex = (index: number): string => CHART_COLOR_VARS[index % CHART_COLOR_VARS.length];
const withColors = (rows: SectionRow[]): ColoredSectionRow[] =>
  rows.map((row, index) => ({ ...row, color: colorForIndex(index) }));
const sumCalls = (rows: SectionRow[]): number =>
  rows.reduce((total, row) => total + row.callCount, 0);
const formatSectionTitle = (key: string): string => formatValue(key);
const SectionBreakdownCard: React.FC<{
  section: ColoredBreakdownSection;
  onFilterByMetadata?: (key: string, value: string) => void;
}> = ({ section, onFilterByMetadata }) => {
  // Whole-section drilldown gate: a navigation handler exists, this is a metadata
  // section (not the non-filterable direction section), and the key is registered
  // as searchable (the only keys the QC list can filter on).
  const sectionDrillable =
    !!onFilterByMetadata && section.metadataKey !== null && section.searchable;

  return (
    <Card className="h-full w-full p-4 shadow-sm" data-export-pdf-block>
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-semibold leading-5 text-foreground">{section.title}</h4>
        </div>

        <DonutBreakdown rows={section.rows} totalCalls={section.totalCalls} title={section.title} />

        <ul className="divide-y">
          {section.rows.map(row => {
            // A row is clickable when the section is drillable and the value is a
            // real value (not the "— (none)"/empty bucket, which isn't a useful
            // equality filter).
            const clickable = sectionDrillable && row.value !== '';
            const label = row.label === '—' ? '— (none)' : formatValue(row.label);

            return (
              <li key={row.label} className="flex items-center gap-2 py-2 first:pt-0 last:pb-0">
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ backgroundColor: row.color }}
                />
                {clickable ? (
                  <button
                    type="button"
                    onClick={() => onFilterByMetadata?.(section.metadataKey as string, row.value)}
                    title={`Filter evaluations by ${section.title}: ${label}`}
                    className="min-w-0 flex-1 truncate text-left text-[13px] font-medium leading-5 text-foreground underline-offset-2 hover:text-primary hover:underline focus-visible:text-primary focus-visible:underline focus-visible:outline-none"
                  >
                    {label}
                  </button>
                ) : (
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium leading-5 text-foreground">
                    {label}
                  </span>
                )}
                <span className="shrink-0 text-[13px] font-semibold leading-5 text-foreground tabular-nums">
                  {row.callCount}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </Card>
  );
};

const INELIGIBLE_REASON_INFO =
  "Rating-Ineligible calls by reason, from the scorecard's own ineligibility checks. " +
  'These calls never enter any average.';

/**
 * "Why calls weren't scored" — its own full-width analytics card breaking down
 * ineligible evaluations by their (server-canonicalized, non-PHI) reason. The
 * donut hole shows the ineligible total ("N not scored"), the legend sits beside
 * it, and the header shows "N of <total calls> (X%)". Static (non-drillable):
 * there is no QC-list filter for ineligibility reason.
 */
export const IneligibleReasonBreakdownSection: React.FC<{
  data?: IneligibleReasonBreakdown;
  /** Total calls in the period — the denominator for the "N of M (X%)" header. */
  totalCalls?: number;
  loading?: boolean;
  exportSectionId?: string;
  onDownload?: (format: ChartDownloadFormat, element?: HTMLElement | null) => void | Promise<void>;
}> = ({ data, totalCalls, loading, exportSectionId, onDownload }) => {
  if (loading) {
    return (
      <ChartCard
        title="Why calls weren't scored"
        info={INELIGIBLE_REASON_INFO}
        exportSectionId={exportSectionId}
      >
        <div className="flex items-center justify-center py-6">
          <Skeleton className="h-36 w-36 rounded-full" />
        </div>
      </ChartCard>
    );
  }

  const reasons = data?.reasons ?? [];
  // Nothing ineligible in the window — omit the card entirely rather than
  // showing an empty donut.
  if (reasons.length === 0) return null;

  // Merge buckets that share a display title (e.g. transfer_call + transfer_event
  // both render as "Transferred Call") so the donut and legend show one row each.
  const byTitle = new Map<string, number>();
  reasons.forEach(reason => {
    const title = formatIneligibleReasonBucketTitle(reason.reason_label);
    byTitle.set(title, (byTitle.get(title) ?? 0) + reason.count);
  });
  const rows: ColoredSectionRow[] = withColors(
    [...byTitle.entries()]
      .map(([label, callCount]) => ({ label, callCount, value: '' }))
      .sort(
        (left, right) => right.callCount - left.callCount || left.label.localeCompare(right.label),
      ),
  );

  const notScored = data?.total ?? sumCalls(rows);
  const badge =
    totalCalls && totalCalls > 0
      ? `${notScored} of ${totalCalls} (${formatShare(notScored, totalCalls)})`
      : `${notScored} not scored`;

  return (
    <ChartCard
      title="Why calls weren't scored"
      info={INELIGIBLE_REASON_INFO}
      exportSectionId={exportSectionId}
      onDownload={onDownload}
      rightSlot={
        <span className="text-xs font-medium leading-5 text-muted-foreground tabular-nums">
          {badge}
        </span>
      }
    >
      <div className="flex flex-col items-center gap-6 py-2 sm:flex-row sm:justify-center sm:gap-12">
        {/* Wrapper neutralizes DonutBreakdown's own mx-auto, which would otherwise
            grab auto side-margins in this flex row and shove the legend to the edge. */}
        <div className="shrink-0">
          <DonutBreakdown
            rows={rows}
            totalCalls={notScored}
            title="Why calls weren't scored"
            center={{ primary: String(notScored), secondary: 'not scored' }}
            rawLabels
          />
        </div>
        {/* One grid so the dot / label / count columns line up across every row;
            sized to content (sm:w-auto) so the count column sits right after the
            longest label instead of drifting to the card edge. */}
        <div className="grid w-full max-w-md grid-cols-[auto_1fr_auto] items-center gap-x-6 gap-y-2 sm:w-auto">
          {rows.map(row => (
            <React.Fragment key={row.label}>
              <span
                aria-hidden
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: row.color }}
              />
              <span className="truncate text-[13px] leading-5 text-foreground">{row.label}</span>
              <span className="text-right text-[13px] font-semibold leading-5 text-foreground tabular-nums">
                {row.callCount}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </ChartCard>
  );
};

export const CallBreakdownGrid: React.FC<CallBreakdownGridProps> = ({
  data,
  loading,
  evaluatedCalls,
  exportSectionId,
  organizationId,
  drilldownScope,
}) => {
  const navigate = useNavigate();

  // The drilldown lands on the QC evaluations list, which requires evaluation read
  // permission. Gate the drilldown so users without it (e.g. the analytics-only
  // role) don't get a link that dead-ends in a 403/empty QC list.
  const { hasPermission: canReadEvaluations } = useAPIPermission(
    API_METHODS.EvaluationService.ListEvaluations,
  );

  // Navigate to the QC list filtered by metadata.<key>=<value>. Only wired for
  // metadata sections when an org id is present (see onFilterByMetadata usage).
  const onFilterByMetadata = React.useCallback(
    (key: string, value: string) => {
      if (!organizationId) return;
      navigate(buildQcMetadataFilterPath(organizationId, key, value, drilldownScope));
    },
    [navigate, organizationId, drilldownScope],
  );
  const canDrilldown = !!organizationId && canReadEvaluations;

  if (loading) {
    return (
      <div data-export-section={exportSectionId}>
        <BreakdownGridSkeleton />
      </div>
    );
  }

  if (!data) {
    return (
      <section className="space-y-4" data-export-pdf-block data-export-section={exportSectionId}>
        <header className="space-y-1">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold leading-5 text-foreground">Call Analytics</h3>
            <InfoTooltip content={CALL_ANALYTICS_INFO} />
          </div>
          <p className="text-xs leading-5 text-muted-foreground">
            Each section, broken down — 0 total calls in the period
          </p>
        </header>
        <ChartEmptyState height={180} />
      </section>
    );
  }

  const directionRows: SectionRow[] = data.by_direction
    .map(d => ({
      label: d.direction || '—',
      callCount: d.call_count,
      value: d.direction ?? '',
    }))
    .sort(
      (left, right) => right.callCount - left.callCount || left.label.localeCompare(right.label),
    );
  const metadataSections: BreakdownSection[] = Object.entries(data.by_metadata ?? {})
    .filter(([key]) => !HIDDEN_METADATA_SECTION_KEYS.has(key))
    .map(([key, rows]) => ({
      title: formatSectionTitle(key),
      rows: toRows(rows),
      metadataKey: key,
      // searchable is a per-key attribute repeated identically on every entry;
      // read it off the first (default false when the key isn't searchable).
      searchable: rows?.[0]?.searchable ?? false,
    }))
    .filter(section => section.rows.length > 0);
  const sections: BreakdownSection[] = [
    {
      title: 'Direction',
      rows: directionRows,
      metadataKey: null,
      searchable: false,
    },
    ...metadataSections,
  ].filter(section => section.rows.length > 0);
  const directionTotal = sumCalls(directionRows);
  const resolvedTotal = evaluatedCalls ?? directionTotal;
  const coloredSections: ColoredBreakdownSection[] = sections.map(section => ({
    ...section,
    rows: withColors(section.rows),
    totalCalls: resolvedTotal,
  }));
  const periodTotalCalls = resolvedTotal;

  return (
    <section className="space-y-4" data-export-pdf-block data-export-section={exportSectionId}>
      <header className="space-y-1">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold leading-5 text-foreground">Call Analytics</h3>
          <InfoTooltip content={CALL_ANALYTICS_INFO} />
        </div>
        <p className="text-xs leading-5 text-muted-foreground">
          Each section, broken down — {periodTotalCalls} total calls in the period
        </p>
      </header>
      {sections.length === 0 ? (
        <ChartEmptyState height={180} />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {coloredSections.map(section => (
            <SectionBreakdownCard
              key={section.title}
              section={section}
              onFilterByMetadata={canDrilldown ? onFilterByMetadata : undefined}
            />
          ))}
        </div>
      )}
    </section>
  );
};
